/**
 * RealtimeManager - Singleton channel manager for Supabase Realtime
 * 
 * Multiplexes all postgres_changes subscriptions onto a single channel,
 * reducing per-user connection count from ~5 to 1.
 * 
 * Uses reference counting: channel opens on first subscriber,
 * closes when last subscriber unregisters.
 */

import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { safeError } from '@/lib/safeError';

type PostgresEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface Subscription {
  id: string;
  table: string;
  event: PostgresEvent;
  filter?: string;
  callback: (payload: RealtimePostgresChangesPayload<any>) => void;
}

let nextId = 0;

export class RealtimeManager {
  private channel: RealtimeChannel | null = null;
  private subscriptions: Map<string, Subscription> = new Map();
  private isSubscribed = false;
  private pendingRebuild = false;

  /**
   * Subscribe to postgres changes on a table.
   * Returns an unsubscribe function.
   */
  subscribe(
    table: string,
    event: PostgresEvent,
    filter: string | undefined,
    callback: (payload: RealtimePostgresChangesPayload<any>) => void
  ): () => void {
    const id = `sub_${++nextId}`;
    const sub: Subscription = { id, table, event, filter, callback };
    this.subscriptions.set(id, sub);

    // Rebuild the channel to include the new listener
    this.rebuild();

    return () => {
      this.subscriptions.delete(id);
      if (this.subscriptions.size === 0) {
        this.teardown();
      }
      // Note: we don't rebuild on unsubscribe to avoid churn.
      // Stale listeners just won't match any callbacks.
    };
  }

  private teardown() {
    if (this.channel) {
      supabase.removeChannel(this.channel);
      this.channel = null;
      this.isSubscribed = false;
    }
  }

  private rebuild() {
    // Debounce multiple subscribes in the same tick
    if (this.pendingRebuild) return;
    this.pendingRebuild = true;

    queueMicrotask(() => {
      this.pendingRebuild = false;
      this.doRebuild();
    });
  }

  private doRebuild() {
    // Tear down old channel
    this.teardown();

    if (this.subscriptions.size === 0) return;

    // Build a new channel with all current subscriptions
    const channel = supabase.channel('global-pg-changes');

    // Collect unique table+event+filter combos to avoid duplicate .on() calls
    const seen = new Set<string>();

    for (const sub of this.subscriptions.values()) {
      const key = `${sub.table}:${sub.event}:${sub.filter || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const config: any = {
        event: sub.event,
        schema: 'public',
        table: sub.table,
      };
      if (sub.filter) {
        config.filter = sub.filter;
      }

      channel.on(
        'postgres_changes',
        config,
        (payload: RealtimePostgresChangesPayload<any>) => {
          this.dispatch(sub.table, payload);
        }
      );
    }

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        this.isSubscribed = true;
      }
    });

    this.channel = channel;
  }

  private dispatch(table: string, payload: RealtimePostgresChangesPayload<any>) {
    const eventType = payload.eventType as PostgresEvent;

    for (const sub of this.subscriptions.values()) {
      if (sub.table !== table) continue;

      // Match event: either wildcard or exact
      if (sub.event !== '*' && sub.event !== eventType) continue;

      // Match filter if present on the subscription
      // Filter matching is handled by Supabase server-side,
      // so we just dispatch to all callbacks for the table+event combo
      try {
        sub.callback(payload);
      } catch (err) {
        console.error(`[RealtimeManager] Callback error for ${table}:${sub.event}:`, safeError(err));
      }
    }
  }

  destroy() {
    this.teardown();
    this.subscriptions.clear();
  }
}
